import { useEffect } from 'react';
import useSWR, { type KeyedMutator } from 'swr';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components';
import { getReportEventsCacheKey } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import { ReportStatus, type ReportWithEvents } from '@/types/report';

interface UseReportDataParams {
    reportId: string;
    minConfidence: number;
    onReportNameResolved?: (name: string | undefined) => void;
}

interface UseReportDataResult {
    report: ReportWithEvents | undefined;
    mutate: KeyedMutator<ReportWithEvents>;
    isInitialLoading: boolean;
    isExportEnabled: boolean;
}

export function useReportData({
    reportId,
    minConfidence,
    onReportNameResolved,
}: UseReportDataParams): UseReportDataResult {
    const { t } = useTranslation(I18nNamespace.REPORTS);

    const {
        data: report,
        isLoading,
        error,
        mutate,
    } = useSWR(
        getReportEventsCacheKey(reportId, minConfidence),
        () =>
            EngineService.getReportWithEvents(reportId, {
                minConfidence,
            }),
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: true,
        },
    );

    useEffect(() => {
        if (error) {
            toast.error(t('detail.failedToLoad'));
        }
    }, [error, t]);

    useEffect(() => {
        onReportNameResolved?.(report?.name);
    }, [report?.name, onReportNameResolved]);

    return {
        report,
        mutate,
        isInitialLoading: !report && isLoading,
        isExportEnabled: report?.status === ReportStatus.COMPLETED,
    };
}
